import React, { useState } from "react";
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, TablePagination, Button, Dialog, DialogTitle,
    DialogContent, DialogContentText, DialogActions, CircularProgress, useMediaQuery, Divider
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faUsersGear } from "@fortawesome/free-solid-svg-icons";
import baseURL from "../../config";
import { toast } from "react-toastify";

// Custom COLORS for a Microsoft-like aesthetic
const MS_COLORS = {
    primary: '#2D4A3D',
    primaryLight: '#EBF5EE',
    text: '#323130',
    textSecondary: '#605D5C',
    background: '#FFFFFF',
    backgroundLight: '#F3F2F1',
    border: '#E1E1E1',
    hoverBackground: '#F0F0F0',
    white: '#FFFFFF',
    accent: '#107C10',
    red: '#D83B01',
    orange: '#FF8C00',
};

// Component now receives props from the parent
const TransactionsManagement = ({ admins, fetchAdmins, processingAction, setProcessingAction }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState("");
    const [selectedAdmin, setSelectedAdmin] = useState(null);

    const isSmallScreen = useMediaQuery("(max-width:600px)");

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    function formatDateTime(isoString) {
        if (!isoString) return "N/A";
        const dateObj = new Date(isoString);
        if (isNaN(dateObj.getTime())) {
            return "Invalid Date";
        }
        return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")} ${String(dateObj.getHours()).padStart(2, "0")}:${String(dateObj.getMinutes()).padStart(2, "0")}:${String(dateObj.getSeconds()).padStart(2, "0")}`;
    }

    const handleOpenDialog = (admin, type) => {
        setSelectedAdmin(admin);
        setDialogType(type);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedAdmin(null);
        setDialogType("");
        setProcessingAction(false);
    };

    const handleDeleteAdmin = async () => {
        if (!selectedAdmin) return;
        setProcessingAction(true);

        try {
            const response = await fetch(`${baseURL}/admin/${selectedAdmin.id}`, {
                method: "DELETE",
                credentials: "include",
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || "Failed to delete admin.");
            }

            toast.success("Admin deleted successfully.");
            // Call the parent's function to re-fetch the data
            await fetchAdmins();
        } catch (error) {
            console.error("Error deleting admin:", error);
            toast.error(error.message || "Network error. Could not delete admin.");
        } finally {
            setProcessingAction(false);
            handleCloseDialog();
        }
    };

    return (
        <Box
            sx={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                border: "1px solid rgba(0,0,0,0.05)",
                p: 3,
                mt: 3,
                mb: 3,
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    color: "#1a1a2e",
                    mb: 3,
                    fontWeight: 800,
                    fontSize: isSmallScreen ? "1.05rem" : "1.2rem",
                    letterSpacing: "-0.3px",
                }}
            >
                Admin Management
            </Typography>

            {/* No internal loading state needed here, it's handled by the parent */}
            {admins.length === 0 ? (
                <Box
                    sx={{
                        textAlign: "center",
                        mt: 3,
                        p: 3,
                        border: `1px dashed ${MS_COLORS.border}`,
                        borderRadius: 1,
                        bgcolor: MS_COLORS.backgroundLight,
                    }}
                >
                    <FontAwesomeIcon icon={faUsersGear} style={{ fontSize: "40px", color: MS_COLORS.textSecondary, marginBottom: "8px" }} />
                    <Typography sx={{ color: MS_COLORS.textSecondary, fontSize: "0.9rem", fontStyle: "italic" }}>
                        No admin accounts available.
                    </Typography>
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: 0, border: `1px solid ${MS_COLORS.border}` }}>
                    <Table sx={{ minWidth: 700 }}>
                        <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", color: MS_COLORS.text }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", color: MS_COLORS.text }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", color: MS_COLORS.text }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", color: MS_COLORS.text }}>Role</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", color: MS_COLORS.text }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", color: MS_COLORS.text }}>Created At</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", color: MS_COLORS.text, textAlign: "center" }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {admins.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((admin) => (
                                <TableRow
                                    key={admin.id}
                                    sx={{
                                        "&:hover": { backgroundColor: MS_COLORS.hoverBackground },
                                    }}
                                >
                                    <TableCell sx={{ fontSize: "0.8rem", py: 1, color: MS_COLORS.textSecondary }}>{admin.id}</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", py: 1, color: MS_COLORS.textSecondary }}>{admin.name}</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", py: 1, color: MS_COLORS.textSecondary }}>{admin.email}</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", py: 1, color: MS_COLORS.textSecondary }}>{admin.role}</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", py: 1, color: admin.status === "active" ? MS_COLORS.accent : MS_COLORS.red }}>
                                        {admin.status}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", py: 1, color: MS_COLORS.textSecondary }}>{formatDateTime(admin.created_at)}</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", py: 1, textAlign: "center" }}>
                                        {admin.role !== "super_Admin" && (
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        fontSize: "0.75rem",
                                                        textTransform: "none",
                                                        color: MS_COLORS.red,
                                                        borderColor: MS_COLORS.red,
                                                        borderRadius: "4px",
                                                        "&:hover": {
                                                            backgroundColor: MS_COLORS.red + '10',
                                                            borderColor: MS_COLORS.red,
                                                        },
                                                        "&:disabled": {
                                                            borderColor: MS_COLORS.border,
                                                            color: MS_COLORS.textSecondary,
                                                        },
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                    startIcon={<FontAwesomeIcon icon={faTrash} />}
                                                    onClick={() => handleOpenDialog(admin, "delete")}
                                                    disabled={processingAction}
                                                >
                                                    Delete
                                                </Button>
                                            </Box>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {admins.length > 0 && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={admins.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions":
                        {
                            fontSize: "0.8rem",
                            color: MS_COLORS.textSecondary,
                        },
                    }}
                />
            )}

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                sx={{
                    "& .MuiDialog-paper": {
                        borderRadius: "8px",
                        padding: "16px",
                        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                        backgroundColor: MS_COLORS.background,
                        border: `1px solid ${MS_COLORS.border}`,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                        textAlign: "left",
                        color: dialogType === "deactivate" ? MS_COLORS.orange : MS_COLORS.red,
                        pb: 1,
                    }}
                >
                    {dialogType === "deactivate" ? "Deactivate Admin" : "Delete Admin"}
                </DialogTitle>
                <Divider sx={{ mb: 2 }} />

                <DialogContent sx={{ padding: "8px 24px", textAlign: "left" }}>
                    <DialogContentText variant="body1" sx={{ color: MS_COLORS.text, fontSize: "0.9rem" }}>
                        {dialogType === "deactivate"
                            ? `Are you sure you want to deactivate ${selectedAdmin?.name}'s account? This will restrict their access to the admin panel.`
                            : `Are you sure you want to permanently delete ${selectedAdmin?.name}'s account? This action cannot be undone.`}
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ justifyContent: "flex-end", pt: 2, px: 3 }}>
                    <Button
                        onClick={handleCloseDialog}
                        variant="outlined"
                        sx={{
                            color: MS_COLORS.text,
                            borderColor: MS_COLORS.border,
                            textTransform: 'none',
                            borderRadius: "4px",
                            fontSize: "0.85rem",
                            px: 2,
                            "&:hover": {
                                borderColor: MS_COLORS.primary,
                                backgroundColor: MS_COLORS.primaryLight + '30',
                            },
                        }}
                        disabled={processingAction}
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={handleDeleteAdmin} // Use the new handleDeleteAdmin function
                        variant="contained"
                        sx={{
                            backgroundColor: MS_COLORS.red,
                            color: MS_COLORS.white,
                            borderRadius: "4px",
                            fontSize: "0.85rem",
                            textTransform: 'none',
                            boxShadow: "none",
                            px: 3,
                            "&:hover": {
                                backgroundColor: MS_COLORS.red,
                                opacity: 0.9,
                                boxShadow: "none",
                            },
                            "&:disabled": {
                                backgroundColor: MS_COLORS.border,
                                color: MS_COLORS.textSecondary,
                            },
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}
                        disabled={processingAction}
                    >
                        {processingAction ? (
                            <>
                                <CircularProgress size={16} sx={{ color: MS_COLORS.white }} />
                                Deleting...
                            </>
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TransactionsManagement;